import React, {useEffect} from 'react';
  import {useApi} from "../../providers/ApiProvider";
  import BusinessesModel from "../../models/BusinessesModel";
  import {useParams} from "react-router-dom";
  import {useBreadcrumbs} from "../../providers/BreadcrumbsProvider";
  import BusinessViewHeader from "./BusinessViewHeader";
  import BusinessViewMenu from "./BusinessViewMenu";
  import {useTranslation} from "react-i18next";
  import {useTranslation as useMyTranslation} from "../../providers/TranslationProvider";
  import Chart from "react-apexcharts";
  import ButtonWithLoading from "../layout/ui/ButtonWithLoading";

  function BusinessesSeoAudit() {
      const {language} = useMyTranslation();
      const {t} = useTranslation();
      const params = useParams();
      const apiContext = useApi();
      const businessesModel = new BusinessesModel(apiContext.api);
      const breadcrumbs = useBreadcrumbs();

      const [initialLoading, setInitialLoading] = React.useState(true);
      const [auditing, setAuditing] = React.useState(false);
      const [auditData, setAuditData] = React.useState(null);
      const [error, setError] = React.useState(null);
      const [expandedSections, setExpandedSections] = React.useState({});

      const setMetaData = (profile) => {
          if (profile !== false) {
              breadcrumbs.update({
                  title: profile.title,
                  breadcrumbs: [
                      {label: t('Businesses List'), url: '/businesses'},
                      {label: profile.title, url: '/businesses/' + params.locationId},
                      {label: t('SEO-Audit')}
                  ]
              });
              document.title = t('SEO-Audit') + ' | ' + profile.title + ' | LocalBoost';
          }
      };

      const onProfileRefresh = (profile) => { setMetaData(profile); };

      const loadSavedAudit = () => {
          setInitialLoading(true);
          setError(null);
          businessesModel.getSavedSeoAudit(params.locationId, (err) => {
              setError(t('Fehler beim Laden des Audits. Bitte versuchen Sie es erneut.'));
          }).then((response) => {
              if (response !== false && response.data) {
                  setAuditData(response.data);
              }
          }).catch(() => {
              setError(t('Fehler beim Laden des Audits. Bitte versuchen Sie es erneut.'));
          }).finally(() => { setInitialLoading(false); });
      };

    const runAudit = () => {
        setAuditing(true);
        setError(null);
        businessesModel.runSeoAudit(params.locationId, (err) => {
            const serverMsg = (err && (err.error || err.message)) || '';
            setError(serverMsg || 'Fehler beim Durchführen des Audits. Bitte versuchen Sie es erneut.');
        }).then((response) => {
            if (response !== false) { setAuditData(response); }
        }).finally(() => { setAuditing(false); });
    };

      useEffect(() => {
          businessesModel.view(params.locationId).then((response) => {
              if (response !== false) { setMetaData(response.data); }
          });
          loadSavedAudit();
      }, [language]);

      const toggleSection = (cat) => {
          setExpandedSections(prev => ({...prev, [cat]: !prev[cat]}));
      };

      const formatDate = (dateStr) => {
          if (!dateStr) return '';
          try {
              const d = new Date(dateStr);
              return d.toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'});
          } catch(e) { return dateStr; }
      };

      const getScoreColor = (score, max) => {
          const pct = max > 0 ? (score / max) * 100 : 0;
          if (pct >= 80) return 'success';
          if (pct >= 50) return 'warning';
          return 'danger';
      };

      const getOverallGrade = (pct) => {
          if (pct >= 90) return {grade: 'A', color: 'success', label: t('Ausgezeichnet')};
          if (pct >= 80) return {grade: 'B', color: 'success', label: t('Sehr gut')};
          if (pct >= 70) return {grade: 'C', color: 'warning', label: t('Gut')};
          if (pct >= 50) return {grade: 'D', color: 'warning', label: t('Verbesserungsfähig')};
          return {grade: 'F', color: 'danger', label: t('Dringend optimieren')};
      };

      const getRadialChart = () => {
          if (!auditData) return null;
          return {
              options: {
                  chart: {type: 'radialBar', fontFamily: 'inherit'},
                  plotOptions: {
                      radialBar: {
                          startAngle: -135, endAngle: 135,
                          hollow: {size: '65%'},
                          track: {background: '#f1f1f1', strokeWidth: '100%'},
                          dataLabels: {
                              name: {show: true, fontSize: '14px', color: '#888', offsetY: 60},
                              value: {show: true, fontSize: '36px', fontWeight: 700, offsetY: -5, formatter: () => auditData.percentage + '%'}
                          }
                      }
                  },
                  fill: {type: 'gradient', gradient: {shade: 'dark', type: 'horizontal', stops: [0, 100], colorStops: [{offset: 0, color: auditData.percentage >= 70 ? '#28a745' : auditData.percentage >= 50 ? '#ffc107' : '#dc3545'}, {offset: 100, color: auditData.percentage >= 70 ? '#20c997' : auditData.percentage >= 50 ? '#fd7e14' : '#e83e8c'}]}},
                  labels: ['SEO Score'],
                  colors: [auditData.percentage >= 70 ? '#28a745' : auditData.percentage >= 50 ? '#ffc107' : '#dc3545'],
              },
              series: [auditData.percentage]
          };
      };

      const radialChart = getRadialChart();
      const grade = auditData ? getOverallGrade(auditData.percentage) : null;

      return (
          <>
              <BusinessViewHeader locationId={params.locationId} onProfileRefresh={onProfileRefresh} />
              <BusinessViewMenu locationId={params.locationId} active="seo-audit" />

              {error && (
                  <div className="alert alert-danger mt-3 d-flex align-items-center" role="alert">
                      <i className="ti ti-alert-circle me-2 fs-5"></i>
                      <div>{error}</div>
                  </div>
              )}

              {initialLoading && (
                  <div className="card rounded-2 border-0 shadow-sm mt-3"><div className="card-body p-5 text-center">
                      <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
                      <p className="mt-2 text-muted">{t('Daten werden geladen...')}</p>
                  </div></div>
              )}

              {!initialLoading && !auditData && !auditing && (
                  <div className="card rounded-2 border-0 shadow-sm mt-3"><div className="card-body p-5 text-center">
                      <i className="ti ti-report-search fs-1 d-block mb-3 text-primary" style={{fontSize: '48px'}}></i>
                      <h5>{t('Lokales SEO-Audit')}</h5>
                      <p className="text-muted mb-4">{t('Prüfen Sie Ihr Google Business Profil auf lokale SEO-Faktoren: Keyword-Nutzung, Kategorie-Optimierung, NAP-Konsistenz und mehr.')}</p>
                      <div className="d-flex justify-content-center"><ButtonWithLoading className="btn btn-primary btn-lg" noOriginalIconClassName={true} iconClassName="ti ti-search me-2" label={t('Audit starten')} loading={auditing} onClick={runAudit} /></div>
                  </div></div>
              )}

              {auditing && (
                  <div className="card rounded-2 border-0 shadow-sm mt-3"><div className="card-body p-5 text-center">
                      <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}><span className="visually-hidden">Loading...</span></div>
                      <h5>{t('SEO-Audit wird durchgeführt...')}</h5>
                      <p className="text-muted">{t('Ihr Google Business Profil wird analysiert.')}</p>
                  </div></div>
              )}

              {auditData && !auditing && (
                  <>
                      {auditData.auditedAt && (
                    <div className="d-flex justify-content-center align-items-center mt-3 gap-3">
                              <span className="text-muted fs-3">
                                  <i className="ti ti-clock me-1"></i>
                                  {t('Letztes Audit')}: {formatDate(auditData.auditedAt)}
                              </span>
                              <ButtonWithLoading className="btn btn-outline-primary btn-sm" noOriginalIconClassName={true} iconClassName="ti ti-refresh me-2" label={t('Audit aktualisieren')} loading={auditing} onClick={runAudit} />
                          </div>
                      )}

                      <div className="row mt-3">
                          <div className="col-lg-4">
                              <div className="card rounded-2 border-0 shadow-sm h-100">
                                  <div className="card-body p-4 text-center">
                                      {radialChart && <Chart type="radialBar" options={radialChart.options} series={radialChart.series} height={250} />}
                                      {grade && (
                                          <div className="mt-2">
                                              <span className={"badge bg-" + grade.color + " fs-5 px-4 py-2"}>{t('Note')}: {grade.grade}</span>
                                              <p className={"mt-2 fw-semibold text-" + grade.color}>{grade.label}</p>
                                          </div>
                                      )}
                                      <p className="text-muted fs-3 mt-1">{auditData.totalScore} / {auditData.maxScore} {t('Punkte')}</p>
                                  </div>
                              </div>
                          </div>
                          <div className="col-lg-8">
                              <div className="card rounded-2 border-0 shadow-sm h-100">
                                  <div className="card-body p-4">
                                      <h6 className="fw-semibold mb-3"><i className="ti ti-list-check me-2"></i>{t('Übersicht')}</h6>
                                      {(auditData.sections || []).map((section, i) => {
                                          const pct = section.max > 0 ? Math.round((section.score / section.max) * 100) : 0;
                                          const color = getScoreColor(section.score, section.max);
                                          return (
                                              <div key={'section-' + i} className="mb-3">
                                                  <div className="d-flex justify-content-between align-items-center mb-1" onClick={() => toggleSection(section.category)} style={{cursor: 'pointer'}}>
                                                      <div className="d-flex align-items-center gap-2">
                                                          <i className={"ti ti-" + section.icon + " text-" + color}></i>
                                                          <span className="fw-semibold fs-3">{section.label}</span>
                                                          {section.issues?.length > 0 && <span className="badge bg-light-danger text-danger fs-1">{section.issues.length}</span>}
                                                      </div>
                                                      <div className="d-flex align-items-center gap-2">
                                                          <span className={"badge bg-" + color}>{pct}%</span>
                                                          <span className="text-muted fs-2">{section.score}/{section.max}</span>
                                                          <i className={"ti fs-5 " + (expandedSections[section.category] ? "ti-chevron-up" : "ti-chevron-down")}></i>
                                                      </div>
                                                  </div>
                                                  <div className="progress mb-1" style={{height: '6px'}}>
                                                      <div className={"progress-bar bg-" + color} style={{width: pct + '%'}}></div>
                                                  </div>
                                              </div>
                                          );
                                      })}
                                  </div>
                              </div>
                          </div>
                      </div>

                      {(auditData.sections || []).map((section, i) => {
                          if (!expandedSections[section.category]) return null;
                          const color = getScoreColor(section.score, section.max);
                          return (
                              <div key={'detail-' + i} className={"card rounded-2 shadow-sm mt-3 border-" + color}>
                                  <div className="card-body p-4">
                                      <h6 className="fw-semibold mb-3">
                                          <i className={"ti ti-" + section.icon + " me-2 text-" + color}></i>
                                          {section.label}
                                          <span className={"badge bg-" + color + " ms-2"}>{section.score}/{section.max}</span>
                                      </h6>

                                      {section.passed?.length > 0 && (
                                          <div className="mb-3">
                                              {section.passed.map((item, j) => (
                                                  <div key={'pass-' + j} className="d-flex align-items-start gap-2 mb-2">
                                                      <i className="ti ti-circle-check text-success fs-5 mt-1"></i>
                                                      <span className="fs-3">{item}</span>
                                                  </div>
                                              ))}
                                          </div>
                                      )}

                                      {section.issues?.length > 0 && (
                                          <div>
                                              {section.issues.map((item, j) => (
                                                  <div key={'issue-' + j} className="d-flex align-items-start gap-2 mb-2">
                                                      <i className="ti ti-alert-triangle text-warning fs-5 mt-1"></i>
                                                      <span className="fs-3">{item}</span>
                                                  </div>
                                              ))}
                                          </div>
                                      )}
                                  </div>
                              </div>
                          );
                      })}
                  </>
              )}
          </>
      );
  }

  export default BusinessesSeoAudit;
  